// The only place that calls `fetch` (docs/spec.md, Interface web), and the only
// XMLHttpRequest: uploads need its progress events.
// Errors from the server always look like {"error": {"code", "message"}}, with a French
// message that can be shown as is.

export interface UserRef {
  id: string;
  username: string;
  display_name: string;
  avatar_url: string | null;
}

export interface Me {
  id: string;
  username: string;
  display_name: string;
  email: string | null;
  is_admin: boolean;
  push_hide_content: boolean;
  avatar_url: string | null;
}

export type ConversationKind = 'public' | 'private' | 'direct' | 'group';
export type Notify = 'all' | 'mentions' | 'none';

export interface Conversation {
  id: string;
  kind: ConversationKind;
  name: string | null;
  title: string;
  topic: string;
  members: UserRef[];
  /** Members who administer the channel. */
  admin_ids: string[];
  last_message_at: string | null;
  created_at: string;
  archived_at: string | null;
  unread_count: number;
  mention_count: number;
  notify: Notify;
  role: 'member' | 'admin';
  last_read_at: string;
  /** Phase 2: a project channel, where tasks live. */
  is_project: boolean;
  open_task_count: number;
}

export interface Reaction {
  emoji: string;
  count: number;
  /** Computed for whoever loaded the message; events carry `user_ids` instead. */
  me: boolean;
  user_ids: string[];
}

export interface Attachment {
  id: string;
  name: string;
  mime: string;
  size_bytes: number;
  has_thumb: boolean;
}

export type TaskStatus = 'todo' | 'doing' | 'done';
export type DisplayStatus = TaskStatus | 'waiting';

export interface TaskInfo {
  title: string;
  requester: UserRef;
  assignee: UserRef | null;
  /** Who has the turn (« la main »); null when done or unassigned. */
  waiting_on: UserRef | null;
  waiting_since: string | null;
  status: TaskStatus;
  display_status: DisplayStatus;
  /** YYYY-MM-DD */
  due_date: string | null;
  position: number;
  done_at: string | null;
  document_count: number;
}

export interface TaskDocument extends Attachment {
  message_id: string;
  created_at: string;
  author: UserRef;
  handoff: boolean;
}

export interface TaskSummary {
  my_turn: number;
  my_turn_todo: number;
  my_turn_doing: number;
  overdue: number;
  due_today: number;
  due_this_week: number;
  waiting_others: number;
  waiting_others_oldest_since: string | null;
  done_this_week: number;
  done_last_week: number;
  by_day: { date: string; count: number }[];
  by_project: { conversation_id: string; name: string; my_turn: number }[];
}

export interface Message {
  id: string;
  conversation_id: string;
  author: UserRef;
  root_id: string | null;
  kind: 'user' | 'system' | 'task';
  body: string;
  payload: Record<string, unknown> | null;
  created_at: string;
  edited_at: string | null;
  deleted: boolean;
  reply_count: number;
  last_reply_at: string | null;
  pinned: boolean;
  pinned_at: string | null;
  /** Null for pins imported from Mattermost. */
  pinned_by: UserRef | null;
  saved: boolean;
  reactions: Reaction[];
  attachments: Attachment[];
  /** For `kind: 'task'` (phase 2). */
  task?: TaskInfo;
}

/** A file with the message it was sent with (files tab, search). */
export interface FileItem extends Attachment {
  message_id: string;
  conversation_id: string;
  root_id: string | null;
  created_at: string;
  author: UserRef;
}

export interface SearchResults {
  total: number;
  conversations: { id: string; count: number }[];
  /** `headline`: escaped text, the matches in <mark>. */
  messages: { message: Message; headline: string }[];
  files: (FileItem & { headline: string | null })[];
  files_total: number;
}

export interface SearchParams {
  q: string;
  conversation_id?: string;
  author_id?: string;
  from?: string;
  to?: string;
  limit?: number;
  offset?: number;
}

export interface SessionInfo {
  id: string;
  device_label: string;
  created_at: string;
  last_seen_at: string;
  current: boolean;
}

export interface Security {
  sessions: SessionInfo[];
  recovery_codes_left: number;
  has_password: boolean;
}

export type AccountStatus = 'active' | 'invited' | 'reset' | 'not_invited' | 'deactivated';

export interface Account {
  id: string;
  username: string;
  display_name: string;
  email: string | null;
  is_admin: boolean;
  is_bot: boolean;
  imported: boolean;
  status: AccountStatus;
  link_expires_at: string | null;
  last_seen_at: string | null;
  created_at: string;
  avatar_url: string | null;
}

export interface Thread {
  root: Message;
  replies: Message[];
  following: boolean;
}

export interface SavedItem {
  saved_at: string;
  message: Message;
}

export interface DirectoryUser {
  id: string;
  username: string;
  display_name: string;
  is_bot: boolean;
  avatar_url: string | null;
}

export interface PublicChannel {
  id: string;
  name: string;
  topic: string;
  member_count: number;
  last_message_at: string | null;
}

export interface Info {
  app_name: string;
  auth_mode: 'dev' | 'password';
  max_upload_mb: number;
  /** Web Push: `null` while browser notifications are off on the server. */
  vapid_public_key: string | null;
}

export interface Activation {
  kind: 'activate' | 'reset';
  username: string;
  display_name: string;
  email: string | null;
  app_name: string;
  password_min_length: number;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

/** Called on any 401 outside the login pages: the session is gone. */
let onUnauthorized: () => void = () => {};
export function setUnauthorizedHandler(handler: () => void): void {
  onUnauthorized = handler;
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method,
      credentials: 'same-origin',
      headers: body === undefined ? {} : { 'Content-Type': 'application/json' },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
  } catch {
    throw new ApiError(0, 'network', 'Serveur injoignable, vérifiez la connexion');
  }
  if (response.status === 204) return undefined as T;
  const data: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const error = (data as { error?: { code?: string; message?: string } } | null)?.error;
    if (response.status === 401 && !path.startsWith('/auth/')) onUnauthorized();
    throw new ApiError(
      response.status,
      error?.code ?? 'unknown',
      error?.message ?? `Erreur ${response.status}`,
    );
  }
  return data as T;
}

/** POST /api/files with progress (0 to 1). `abort` cancels the upload. */
export function uploadFile(
  conversationId: string,
  file: File,
  onProgress: (fraction: number) => void,
): { done: Promise<Attachment>; abort: () => void } {
  const xhr = new XMLHttpRequest();
  const done = new Promise<Attachment>((resolve, reject) => {
    xhr.open('POST', '/api/files');
    xhr.withCredentials = true;
    xhr.responseType = 'json';
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(e.loaded / e.total);
    };
    xhr.onload = () => {
      if (xhr.status === 201) return resolve(xhr.response as Attachment);
      const error = (xhr.response as { error?: { code?: string; message?: string } } | null)?.error;
      if (xhr.status === 401) onUnauthorized();
      reject(new ApiError(xhr.status, error?.code ?? 'unknown', error?.message ?? `Erreur ${xhr.status}`));
    };
    xhr.onerror = () => reject(new ApiError(0, 'network', 'Envoi interrompu, vérifiez la connexion'));
    xhr.onabort = () => reject(new ApiError(0, 'aborted', 'Envoi annulé'));
    const form = new FormData();
    // The server needs the conversation before the file.
    form.append('conversation_id', conversationId);
    form.append('file', file, file.name);
    xhr.send(form);
  });
  return { done, abort: () => xhr.abort() };
}

export const fileUrl = (id: string) => `/api/files/${encodeURIComponent(id)}`;
export const thumbUrl = (id: string) => `/api/files/${encodeURIComponent(id)}/thumb`;

const get = <T>(path: string) => request<T>('GET', path);
const post = <T>(path: string, body?: unknown) => request<T>('POST', path, body ?? {});
const put = <T>(path: string, body: unknown) => request<T>('PUT', path, body);
const patch = <T>(path: string, body: unknown) => request<T>('PATCH', path, body);
const del = <T>(path: string) => request<T>('DELETE', path);

export const api = {
  info: () => get<Info>('/info'),
  me: () => get<Me>('/me'),
  users: () => get<DirectoryUser[]>('/users'),

  // Authentication
  devUsers: () => get<{ id: string; username: string; display_name: string }[]>('/auth/dev/users'),
  devLogin: (username: string) => post<{ user: Me }>('/auth/dev/login', { username }),
  login: (email: string, password: string) =>
    post<{ step: 'totp'; challenge: string }>('/auth/login', { email, password }),
  totp: (challenge: string, code: string) => post<{ user: Me }>('/auth/totp', { challenge, code }),
  logout: () => post<void>('/auth/logout'),
  activation: (token: string) => get<Activation>(`/auth/activation/${encodeURIComponent(token)}`),
  activationTotp: (token: string) =>
    post<{ secret: string; otpauth_url: string }>(`/auth/activation/${encodeURIComponent(token)}/totp`),
  activate: (token: string, password: string, totp_secret: string, totp_code: string) =>
    post<{ recovery_codes: string[] }>(`/auth/activation/${encodeURIComponent(token)}`, {
      password,
      totp_secret,
      totp_code,
    }),

  // Conversations
  conversations: () => get<Conversation[]>('/conversations'),
  conversation: (id: string) => get<Conversation>(`/conversations/${id}`),
  browse: () => get<PublicChannel[]>('/conversations/browse'),
  createConversation: (body: {
    kind: ConversationKind;
    name?: string;
    topic?: string;
    member_ids?: string[];
  }) => post<Conversation>('/conversations', body),
  updateConversation: (id: string, body: { name?: string; topic?: string; archived?: boolean }) =>
    patch<Conversation>(`/conversations/${id}`, body),
  addMembers: (id: string, user_ids: string[]) =>
    post<Conversation>(`/conversations/${id}/members`, { user_ids }),
  removeMember: (id: string, userId: string) => del<void>(`/conversations/${id}/members/${userId}`),
  markRead: (id: string, message_id: string) =>
    put<{ conversation_id: string; last_read_at: string }>(`/conversations/${id}/read`, { message_id }),
  setNotify: (id: string, notify: Notify) => put<unknown>(`/conversations/${id}/notify`, { notify }),

  // Messages
  messages: (id: string, cursor?: { before?: string; after?: string; around?: string }, limit = 50) => {
    const params = new URLSearchParams({ limit: String(limit) });
    if (cursor?.before) params.set('before', cursor.before);
    if (cursor?.after) params.set('after', cursor.after);
    if (cursor?.around) params.set('around', cursor.around);
    return get<Message[]>(`/conversations/${id}/messages?${params}`);
  },
  send: (id: string, body: string, client_id: string, root_id: string | null = null, attachment_ids: string[] = []) =>
    post<Message>(`/conversations/${id}/messages`, { body, client_id, root_id, attachment_ids }),
  thread: (messageId: string) => get<Thread>(`/messages/${messageId}/thread`),
  edit: (messageId: string, body: string) => patch<Message>(`/messages/${messageId}`, { body }),
  remove: (messageId: string) => del<void>(`/messages/${messageId}`),
  react: (messageId: string, emoji: string, on: boolean) =>
    request<{ message_id: string; reactions: Reaction[] }>(
      on ? 'PUT' : 'DELETE',
      `/messages/${messageId}/reactions/${encodeURIComponent(emoji)}`,
      on ? {} : undefined,
    ),
  save: (messageId: string, on: boolean) =>
    request<{ message_id: string; saved: boolean }>(on ? 'PUT' : 'DELETE', `/messages/${messageId}/saved`, on ? {} : undefined),
  pin: (messageId: string, on: boolean) =>
    request<Message>(on ? 'PUT' : 'DELETE', `/messages/${messageId}/pin`, on ? {} : undefined),
  pinned: (conversationId: string) => get<Message[]>(`/conversations/${conversationId}/pinned`),
  search: (params: SearchParams) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) if (value !== undefined && value !== '') query.set(key, String(value));
    return get<SearchResults>(`/search?${query}`);
  },
  files: (conversationId: string, params: { kind?: string; q?: string; before?: string; limit?: string } = {}) => {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(params)) if (value) query.set(key, value);
    return get<{ total: number; items: FileItem[] }>(`/conversations/${conversationId}/files?${query}`);
  },
  /** A document's text, for the preview. */
  fileText: async (id: string): Promise<string> => {
    let response: Response;
    try {
      response = await fetch(fileUrl(id), { credentials: 'same-origin' });
    } catch {
      throw new ApiError(0, 'network', 'Serveur injoignable, vérifiez la connexion');
    }
    if (!response.ok) throw new ApiError(response.status, 'unknown', 'Fichier introuvable');
    return response.text();
  },
  // Tasks (phase 2)
  createTask: (
    conversationId: string,
    body: { title: string; assignee_id?: string | null; requester_id?: string; due_date?: string | null; body?: string; attachment_ids?: string[]; client_id: string },
  ) => post<Message>(`/conversations/${conversationId}/tasks`, body),
  board: (conversationId: string) => get<Message[]>(`/conversations/${conversationId}/tasks`),
  updateTask: (
    id: string,
    change: Partial<{ title: string; requester_id: string; assignee_id: string | null; status: TaskStatus; due_date: string | null; position: number }>,
  ) => patch<Message>(`/tasks/${id}`, change),
  handoff: (id: string, to_user_id: string, body: string, attachment_ids: string[], client_id: string) =>
    post<Message>(`/tasks/${id}/handoff`, { to_user_id, body, attachment_ids, client_id }),
  takeTurn: (id: string) => post<Message>(`/tasks/${id}/take`),
  taskDocuments: (id: string) => get<TaskDocument[]>(`/tasks/${id}/documents`),
  myTasks: (view: 'my_turn' | 'waiting_others' | 'done') => get<Message[]>(`/me/tasks?view=${view}`),
  taskSummary: () => get<TaskSummary>('/me/tasks/summary'),
  setProject: (conversationId: string, is_project: boolean) =>
    patch<Conversation>(`/conversations/${conversationId}`, { is_project }),

  // Profile
  updateProfile: (change: { display_name?: string; push_hide_content?: boolean }) => patch<Me>('/me', change),

  // Notifications
  registerDevice: (platform: 'web' | 'ios' | 'android', token: string) =>
    post<{ id: string; platform: string }>('/devices', { platform, token }),
  removeDevice: (id: string) => del<void>(`/devices/${encodeURIComponent(id)}`),
  setAvatar: async (file: File): Promise<Me> => {
    const form = new FormData();
    form.append('file', file, file.name);
    let response: Response;
    try {
      response = await fetch('/api/me/avatar', { method: 'PUT', body: form, credentials: 'same-origin' });
    } catch {
      throw new ApiError(0, 'network', 'Serveur injoignable, vérifiez la connexion');
    }
    const data: unknown = await response.json().catch(() => null);
    if (!response.ok) {
      const error = (data as { error?: { code?: string; message?: string } } | null)?.error;
      throw new ApiError(response.status, error?.code ?? 'unknown', error?.message ?? `Erreur ${response.status}`);
    }
    return data as Me;
  },
  removeAvatar: () => del<Me>('/me/avatar'),

  // Security page
  security: () => get<Security>('/me/security'),
  revokeSession: (id: string) => del<void>(`/me/sessions/${id}`),
  changePassword: (current_password: string, new_password: string) =>
    put<void>('/me/password', { current_password, new_password }),
  newRecoveryCodes: (password: string) => post<{ recovery_codes: string[] }>('/me/recovery-codes', { password }),

  // Administration
  accounts: () => get<Account[]>('/admin/users'),
  createAccount: (body: { email: string; display_name: string; username?: string; is_admin: boolean }) =>
    post<{ account: Account; link: string }>('/admin/users', body),
  inviteAccount: (id: string, email?: string) =>
    post<{ account: Account; link: string }>(`/admin/users/${id}/invite`, email ? { email } : {}),
  resetAccount: (id: string) => post<{ account: Account; link: string }>(`/admin/users/${id}/reset`),
  updateAccount: (id: string, change: { deactivated?: boolean; is_admin?: boolean }) =>
    patch<Account>(`/admin/users/${id}`, change),

  saved: (before?: string) => get<SavedItem[]>(`/me/saved${before ? `?before=${before}` : ''}`),

  // Realtime
  wsTicket: () => post<{ ticket: string }>('/ws/ticket'),
};
